// components/AuthGate.tsx — enforce auth + the setup gate (spec §4.4, §4.5).
import { Navigate, Outlet } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AppLayout } from './AppLayout';
import { Wordmark } from './Wordmark';
import { useAuth } from '../hooks/useAuth';
import { SetupProvider, useSetupContext } from '../app/SetupContext';
import { Login } from '../pages/app/Login';

export function Splash({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex min-h-full items-center justify-center">
      <div className="text-center opacity-70">
        <Wordmark className="text-2xl" />
        <p className="mt-3 text-sm">{label}</p>
      </div>
    </div>
  );
}

/** Top-level app gate: loading → splash; unauthenticated → login; else the app shell. */
export function AuthGate() {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Login />;
  return (
    <SetupProvider>
      <AppLayout />
    </SetupProvider>
  );
}

/** Wrap setup-gated routes: block until Key1 is configured (spec §4.4). */
export function RequireSetup({ children }: { children: ReactNode }) {
  const { status, loading } = useSetupContext();
  // Only splash on the INITIAL load (status still null). Later refreshes keep the
  // page mounted so in-flight actions and their status messages aren't lost.
  if (loading && !status) return <Splash label="Checking setup…" />;
  if (!status?.key1Configured) return <Navigate to="/app/setup" replace />;
  return <>{children}</>;
}

export { Outlet };
